import React, { useState } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  IconButton,
  Drawer,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Devices,
  BarChart,
  Business,
  ScreenShare,
  Logout,
  Menu as MenuIcon,
  Close,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { tokens } from '../theme';
import ruralCareLogo from '../assets/transparente.png';

const SIDEBAR_WIDTH = 240;

const NAV_ITEMS = [
  { id: 'dashboard',  label: 'Dashboard',        Icon: DashboardIcon },
  { id: 'devices',    label: 'Dispositivos',      Icon: Devices },
  { id: 'stats',      label: 'Estadísticas',      Icon: BarChart },
  { id: 'saas',       label: 'SaaS',              Icon: Business },
  { id: 'remote',     label: 'Soporte Remoto',    Icon: ScreenShare },
];

// ─── Sidebar content ─────────────────────────────────────────────────────────
const SidebarContent = ({ currentView, onNavigate, onClose }) => {
  const { user, logout } = useAuth();

  const roleLabel =
    user?.role === 'master_admin' ? 'Master Admin'
    : user?.role === 'admin'     ? 'Admin'
    :                              'Viewer';

  const initials = (user?.username || 'U')
    .split(/[\s_-]/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: tokens.color.ink.primary,
        color: tokens.color.ink.inverse,
        userSelect: 'none',
      }}
    >
      {/* Logo / branding */}
      <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src={ruralCareLogo}
              alt="RuralCare"
              sx={{
                width: 32,
                height: 32,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.9375rem',
                  lineHeight: 1.2,
                  color: '#fff',
                  letterSpacing: '-0.01em',
                }}
              >
                RuralCare
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  color: 'rgba(255,255,255,0.45)',
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              >
                Admin Panel
              </Typography>
            </Box>
          </Box>
          {/* Botón cerrar — solo en mobile */}
          {onClose && (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: 'rgba(255,255,255,0.5)', display: { md: 'none' } }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2 }} />

      {/* Nav */}
      <Box sx={{ flex: 1, px: 1.5, py: 2, overflowY: 'auto' }}>
        <List disablePadding>
          {NAV_ITEMS.map(({ id, label, Icon }) => {
            const isActive = currentView === id;
            return (
              <ListItemButton
                key={id}
                selected={isActive}
                onClick={() => {
                  onNavigate(id);
                  onClose?.();
                }}
                sx={{
                  borderRadius: tokens.radius.md,
                  mb: '2px',
                  px: 1.5,
                  py: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.06)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: tokens.color.brand[500],
                    '&:hover': { backgroundColor: tokens.color.brand[400] },
                    '& .MuiListItemIcon-root': { color: '#fff' },
                    '& .MuiListItemText-primary': {
                      color: '#fff',
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: isActive ? '#fff' : 'rgba(255,255,255,0.45)' }}>
                  <Icon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.65)',
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* User block */}
      <Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2 }} />
        <Box sx={{ px: 2, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.75rem',
                fontWeight: 700,
                backgroundColor: tokens.color.brand[600],
                color: '#fff',
                flexShrink: 0,
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.username}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  color: 'rgba(255,255,255,0.4)',
                  fontWeight: 500,
                }}
              >
                {roleLabel}
              </Typography>
            </Box>
            <Tooltip title="Cerrar sesión">
              <IconButton
                size="small"
                onClick={logout}
                sx={{
                  color: 'rgba(255,255,255,0.45)',
                  '&:hover': {
                    color: tokens.color.status.offline.bg,
                    backgroundColor: 'rgba(255,255,255,0.06)',
                  },
                }}
              >
                <Logout sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── AppLayout ────────────────────────────────────────────────────────────────
const AppLayout = ({ currentView, onNavigate, children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: tokens.color.surface.default }}>
      {/* Sidebar — permanente en desktop */}
      <Box
        component="nav"
        sx={{
          width: { md: SIDEBAR_WIDTH },
          flexShrink: { md: 0 },
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            elevation: 0,
            sx: {
              width: SIDEBAR_WIDTH,
              border: 'none',
              boxShadow: tokens.shadow.lg,
            },
          }}
          sx={{ display: { xs: 'block', md: 'none' } }}
        >
          <SidebarContent
            currentView={currentView}
            onNavigate={onNavigate}
            onClose={() => setMobileOpen(false)}
          />
        </Drawer>

        {/* Desktop permanente */}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            width: SIDEBAR_WIDTH,
            zIndex: 100,
          }}
        >
          <SidebarContent currentView={currentView} onNavigate={onNavigate} />
        </Box>
      </Box>

      {/* Contenido principal */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          ml: { md: `${SIDEBAR_WIDTH}px` },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Topbar mobile — solo para abrir el drawer */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            height: 52,
            px: 2,
            borderBottom: `1px solid ${tokens.color.border.default}`,
            backgroundColor: tokens.color.surface.paper,
            position: 'sticky',
            top: 0,
            zIndex: 50,
            gap: 1.5,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setMobileOpen(true)}
            sx={{ color: tokens.color.ink.secondary }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontSize: '0.9375rem' }}>
            RuralCare
          </Typography>
        </Box>

        {/* Page content */}
        <Box sx={{ flex: 1, p: { xs: 2, sm: 3, lg: 4 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AppLayout;
